import React, { useState } from 'react'
import { Card, CardContent } from 'components/ui/Card'
import { Badge } from 'components/ui/Badge'
import { Button } from 'components/ui/Button'
import { type Candidate } from 'lib/api'
import { getStatusColor, formatDate } from 'lib/utils'
import { useAuth } from 'contexts/AuthContext'
import {
  Edit,
  Trash2,
  Mail,
  MapPin
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface CandidateCardProps {
  candidate: Candidate
  onView: (candidate: Candidate) => void
  onEdit: (candidate: Candidate) => void
  onDelete: (candidate: Candidate) => void
  isSelected?: boolean
  selectionMode?: boolean
}

export function CandidateCard({ candidate, onView, onEdit, onDelete, isSelected, selectionMode }: CandidateCardProps) {
  const { hasPermission } = useAuth()
  const { t } = useTranslation()
  const [showDupPopover, setShowDupPopover] = useState(false)
  const hiddenDuplicates: Array<{ id: number; name: string; role: string }> = (candidate as any).hiddenDuplicates || []
  
  const canEditCandidates = hasPermission('edit_candidates')
  const canDeleteCandidates = hasPermission('delete_candidates')
  
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => onView(candidate)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onView(candidate)
        }
      }}
      className={`transition-all duration-200 cursor-pointer group overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
        selectionMode && isSelected
          ? 'ring-2 ring-primary shadow-md bg-primary/5 border-primary/50'
          : selectionMode
          ? 'hover:shadow-md hover:ring-1 hover:ring-primary/30 border-transparent'
          : 'hover:shadow-lg'
      }`}
    >
      <CardContent className="p-3 overflow-hidden">
        {/* Header with avatar */}
        <div className="flex items-start mb-2 gap-2">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <img
              src={candidate.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(candidate.name)}&background=random`}
              alt={candidate.name}
              className="h-10 w-10 rounded-full object-cover border-2 border-border flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                <h3 className="font-semibold text-sm text-foreground truncate">
                  {candidate.name}
                </h3>
                {hiddenDuplicates.length > 0 && (
                  <div className="relative inline-block" onClick={(e) => { e.stopPropagation(); setShowDupPopover(v => !v) }}>
                    <Badge variant="outline" className="text-[9px] px-1 py-0 cursor-pointer">
                      +{hiddenDuplicates.length} {t('candidates.hiddenDuplicates', { defaultValue: 'autres' })}
                    </Badge>
                    {showDupPopover && (
                      <div className="absolute z-50 mt-1 w-56 rounded-md border border-border bg-popover text-popover-foreground shadow-md">
                        <div className="p-2">
                          <div className="text-[11px] font-medium mb-1">
                            {t('candidates.hiddenDuplicatesTitle', { defaultValue: 'Candidats masqués' })}
                          </div>
                          <ul className="space-y-1">
                            {hiddenDuplicates.slice(0, 5).map((dup) => (
                              <li key={dup.id} className="text-[11px] text-muted-foreground truncate">
                                {dup.name} {dup.role ? `- ${dup.role}` : ''}
                              </li>
                            ))}
                            {hiddenDuplicates.length > 5 && (
                              <li className="text-[11px] text-muted-foreground">+{hiddenDuplicates.length - 5} {t('common.more', { defaultValue: 'plus' })}</li>
                            )}
                          </ul>
                          <div className="mt-2 flex justify-end">
                            <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" onClick={(e) => { e.stopPropagation(); setShowDupPopover(false) }}>
                              {t('common.close', { defaultValue: 'Fermer' })}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {candidate.role}
              </p>
              {candidate.source && (
                <Badge variant="outline" className="mt-0.5 text-[9px] uppercase tracking-wide px-1 py-0">
                  {t(`candidates.sources.${candidate.source}`, { defaultValue: candidate.source.replace(/_/g, ' ') })}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Contact info - only if available */}
        {(candidate.email || candidate.location) && (
          <div className="space-y-0.5 mb-2">
            {candidate.email && (
              <div className="flex items-center space-x-1 text-[10px] text-muted-foreground">
                <Mail className="h-2.5 w-2.5 flex-shrink-0" />
                <span className="truncate min-w-0 flex-1">{candidate.email}</span>
              </div>
            )}
            {candidate.location && (
              <div className="flex items-center space-x-1 text-[10px] text-muted-foreground">
                <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
                <span className="truncate min-w-0 flex-1">{candidate.location}</span>
              </div>
            )}
          </div>
        )}

        {/* Bio replacing Skills */}
        {candidate.bio && (
          <p className="text-[10px] text-muted-foreground mb-2 line-clamp-3 leading-snug" title={candidate.bio}>
            {candidate.bio}
          </p>
        )}

        {/* Social Media Links */}
        {(candidate.linkedin_url || candidate.github_url || candidate.twitter_url || candidate.portfolio_url) && (
          <div className="flex items-center space-x-1.5 mb-2 pt-1.5 border-t border-border">
            {candidate.linkedin_url && (
              <a
                href={candidate.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors flex-shrink-0"
                title="LinkedIn"
              >
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            )}
            {candidate.github_url && (
              <a
                href={candidate.github_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-gray-800 dark:text-gray-200 hover:text-gray-600 dark:hover:text-gray-400 transition-colors flex-shrink-0"
                title="GitHub"
              >
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
              </a>
            )}
            {candidate.twitter_url && (
              <a
                href={candidate.twitter_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-blue-400 hover:text-blue-500 transition-colors flex-shrink-0"
                title="Twitter"
              >
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                </svg>
              </a>
            )}
            {candidate.portfolio_url && (
              <a
                href={candidate.portfolio_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 transition-colors flex-shrink-0"
                title="Portfolio"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </a>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-1.5 pt-1.5 border-t border-border">
          {canEditCandidates && (
            <Button
              variant="outline"
              size="sm"
              onClick={(event) => {
                event.stopPropagation()
                onEdit(candidate)
              }}
              className="h-7 px-2"
            >
              <Edit className="h-3 w-3" />
            </Button>
          )}
          {canDeleteCandidates && (
            <Button
              variant="outline"
              size="sm"
              onClick={(event) => {
                event.stopPropagation()
                onDelete(candidate)
              }}
              className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
